/**
 * RogerDoc account-link API service.
 *
 * Wraps the RogerEMR backend endpoints that proxy to RogerDoc's internal
 * account-link API. RogerDoc is the source of truth for the `roger_emr`
 * association — we never persist a local copy in RogerEMR.
 */

import { client } from './api';

export interface RogerDocAccountLink {
  user_email: string;
  roger_emr_agency_id: string;
}

export interface RogerDocClinicianAccount {
  id: string;
  username: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  clinician_title?: string | null;
  active: boolean;
  admin: boolean;
  roger_emr?: RogerDocAccountLink | null;
  /** Only set on the create response; null when RogerDoc did not report it. */
  setup_email_sent?: boolean | null;
}

export interface CreateRogerDocAccountParams {
  user_email: string;
  first_name: string;
  last_name: string;
  email?: string | null;
  clinician_title?: string;
  /** Optional explicit username; RogerDoc derives one from the name when omitted. */
  username?: string | null;
}

/** List every RogerDoc clinician account (and its link) for the current agency. */
export const listRogerDocAccountLinks = async (): Promise<
  RogerDocClinicianAccount[]
> => {
  const response = await client.request({
    method: 'GET',
    url: '/rogerdoc/account-links',
    throwOnError: true,
  });
  return (response.data as RogerDocClinicianAccount[]) ?? [];
};

/** Link an existing RogerDoc account to the given RogerEMR user (by email). */
export const assignRogerDocAccountLink = async (
  accountId: string,
  userEmail: string
): Promise<RogerDocClinicianAccount> => {
  const response = await client.request({
    method: 'PUT',
    url: `/rogerdoc/account-links/${accountId}`,
    body: { user_email: userEmail },
    throwOnError: true,
  });
  return response.data as RogerDocClinicianAccount;
};

/** Clear the RogerEMR association on a RogerDoc account. */
export const clearRogerDocAccountLink = async (
  accountId: string
): Promise<RogerDocClinicianAccount> => {
  const response = await client.request({
    method: 'DELETE',
    url: `/rogerdoc/account-links/${accountId}`,
    throwOnError: true,
  });
  return response.data as RogerDocClinicianAccount;
};

/** Create a new RogerDoc account and link it to the given RogerEMR user. */
export const createRogerDocAccountLink = async (
  params: CreateRogerDocAccountParams
): Promise<RogerDocClinicianAccount> => {
  const response = await client.request({
    method: 'POST',
    url: '/rogerdoc/account-links',
    body: params,
    throwOnError: true,
  });
  return response.data as RogerDocClinicianAccount;
};

/**
 * Build a map of lowercased RogerEMR user email -> linked RogerDoc accounts,
 * scoped to the current RogerEMR agency. A single email may map to several
 * accounts (one RogerEMR user can be linked to multiple RogerDoc accounts).
 */
export const buildRogerDocLinksByEmail = (
  accounts: RogerDocClinicianAccount[],
  rogerEmrAgencyId: string | undefined
): Record<string, RogerDocClinicianAccount[]> => {
  const byEmail: Record<string, RogerDocClinicianAccount[]> = {};
  if (!rogerEmrAgencyId) return byEmail;
  for (const account of accounts) {
    const link = account.roger_emr;
    if (!link || link.roger_emr_agency_id !== rogerEmrAgencyId) continue;
    const key = link.user_email.toLowerCase();
    (byEmail[key] ??= []).push(account);
  }
  return byEmail;
};
